import type { GetOrdersResponse } from '@/api/useOrders/responses'
import { humanReadableSecondDifferenceFromToday } from '@/utils/general'
import type { useMutation } from '@tanstack/react-query'
import type { GridApi, IRowNode } from 'ag-grid-community'
import { notification } from 'antd'
import moment from 'moment'
import type { MutableRefObject, RefObject } from 'react'

export const sendToCarrier = (
  row: Gravitate.Order,
  orderStatus: string,
  force: boolean,
  mutateAsync: ReturnType<typeof useMutation>['mutateAsync'],
  gridAPI: GridApi,
  sendWithoutEmail: boolean
) => {
  mutateAsync({
    order_ids: [row.id],
    force,
    orderStatus,
    send_email: !sendWithoutEmail,
  })
    .then((res) => {
      gridAPI.redrawRows()
      notification.open({
        message: sendWithoutEmail ? 'Carrier Order Marked Sent' : 'Carrier Order Sent',
        description: sendWithoutEmail
          ? `Order ${row.number} has been marked as sent.`
          : `Order ${row.number} has been sent ${row.carrier}.`,
      })
    })
    .catch((err) => {})
}

export const sendToCarriers = async (
  selectedRows: string[],
  gridAPIRef: MutableRefObject<GridApi<Gravitate.Order> | null>,
  mutateAsync: ReturnType<typeof useMutation>['mutateAsync'],
  instant: boolean
) => {
  if (!selectedRows?.length) return
  try {
    // For right now we only support 'first sends' in the multi-select, so always send false.
    await mutateAsync({ order_ids: selectedRows, force: false, instant })

    const row = gridAPIRef?.current?.getRowNode(selectedRows[0])
    notification.open({
      message: 'Carrier Orders Sent',
      description:
        selectedRows?.length > 1
          ? `${selectedRows?.length} orders have been sent to carriers.`
          : `A notification has been sent to ${row?.carrier}`,
    })
  } catch (error) {
    notification.open({
      message: 'Carrier Send Orders',
      description: 'Failed to send orders',
    })
    console.error(error)
  }
}

export const completeOrder = (
  row: Gravitate.Order,
  orderStatus: string,
  mutateAsync: ReturnType<typeof useMutation>['mutateAsync'],
  api: GridApi
) => {
  mutateAsync({ order_ids: [row.id], orderStatus }).then(() => {
    api.refreshCells({ force: true })
    if (orderStatus !== 'complete' || orderStatus !== 'all_od') {
      notification.open({
        message: 'Order Completed',
        description: `Order# ${row.number} was successfully completed.`,
        stack: { threshold: 1 },
        duration: 6,
      })
    } else {
      notification.open({
        message: 'Order Completed',
        stack: { threshold: 1 },
        description: `Order# ${row.number} is now complete and has moved to the "Completed" report.`,
        duration: 6,
      })
    }
  })
}
export const getOnDemandStatus = (order: { state: string; carrier_notify_state: string }) => {
  if (order.state === 'complete') {
    return 'complete'
  }
  if (order.carrier_notify_state === 'none') {
    return order.state
  }
  return order.carrier_notify_state
}

export const removeRows = (rows: IRowNode[], gridAPIRef: RefObject<GridApi>) => {
  gridAPIRef?.current?.applyTransaction({ remove: rows })
}
export const updateRows = (rows: Gravitate.Order[], gridAPIRef: React.MutableRefObject<GridApi>) => {
  gridAPIRef?.current.applyTransaction({ update: rows })
}

export const getLoadedPercent = (schedule: Gravitate.Schedule) =>
  (schedule?.loaded_miles / (schedule?.total_miles || 1)) * 100

export const driverSortOptions = {
  Name: 'driver_name',
  Depot: 'home_location',
  'Start Time': 'shift_start',
  'Loaded (%) - Ascending': 'loaded_percent',
  'Loaded (%) - Descending': 'loaded_percent_desc',
  'Shift Utilization - Ascending': 'driver_utilization',
  'Shift Utilization - Descending': 'driver_utilization_desc',
}

export function getSortFn(sortKey: string) {
  const isDescending = sortKey.includes('desc')
  switch (sortKey) {
    case driverSortOptions['Loaded (%) - Ascending']:
    case driverSortOptions['Loaded (%) - Descending']:
      return (a: Gravitate.Schedule, b: Gravitate.Schedule) =>
        getLoadedPercent(a) > getLoadedPercent(b) ? (isDescending ? -1 : 1) : isDescending ? 1 : -1
    case driverSortOptions['Shift Utilization - Ascending']:
    case driverSortOptions['Shift Utilization - Descending']:
      return (a: Gravitate.Schedule, b: Gravitate.Schedule) =>
        a?.driver_utilization > b?.driver_utilization ? (isDescending ? -1 : 1) : isDescending ? 1 : -1
    default:
      return (a: Record<string, any>, b: Record<string, any>) => (a[sortKey] > b[sortKey] ? 1 : -1)
  }
}

export const getTimeSinceEmailSent = (data: Gravitate.Order) => {
  const seconds = moment.duration(moment(new Date()).diff(data.carrier_updated)).asSeconds()
  return humanReadableSecondDifferenceFromToday(Math.ceil(seconds))
}

export const DRIVER_UTILIZATION_STATUS = {
  underutilized: 'underutilized',
  optimal: 'optimal',
  close_to_max: 'close_to_max',
  overutilized: 'overutilized',
  not_utilized: 'not_utilized',
}

export const DRIVER_UTILIZATION_COLORS = {
  underutilized: 'var(--theme-color-1)',
  optimal: 'var(--theme-color-1)',
  close_to_max: 'var(--theme-color-1)',
  overutilized: 'var(--theme-error)',
  not_utilized: 'var(--gray-600)',
}
export const DRIVER_UTILIZATION_DARK_COLORS = {
  underutilized: 'var(--gray-800)',
  optimal: 'var(--gray-800)',
  close_to_max: 'var(--gray-800)',
  overutilized: 'var(--theme-error)',
  not_utilized: 'var(--gray-600)',
}

export const getRowId = (data: Gravitate.Order, isByOrderView: boolean) => {
  return isByOrderView ? data.id : `${data.order_id}-${data.tank_id}`
}

export const getStorageKey = (key: string, isByOrderView: boolean) =>
  isByOrderView ? `order_management_order_view_${key}` : `order_management_tank_view_${key}`

export const extractTankDetails = (data: Gravitate.Order) => {
  return data.details?.map((detail: Gravitate.Order) => ({
    ...data,
    ...detail,
    dispatch_window_start: data.dispatch_window_start,
    dispatch_window_end: data.dispatch_window_end,
    tank_dispatch_window_start: detail.dispatch_window_start,
    tank_dispatch_window_end: detail.dispatch_window_end,
    tank_carrier_window_start: detail.dispatch_window_start,
    tank_carrier_window_end: detail.dispatch_window_end,
  }))
}

export const getOrderProductRowData = (data: GetOrdersResponse | undefined, isByOrderView: boolean) => {
  if (!data) return []
  return isByOrderView
    ? data.rows.map((order) => ({
        ...order,
        details: extractTankDetails(order),
      }))
    : data.rows.flatMap(extractTankDetails)
}
